import React, { useState, ChangeEvent, useCallback, useEffect } from 'react';
import { Box, CircularProgress, FormControl, styled, MenuItem } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import Typography from '@mui/material/Typography';
import { InputText } from '../../../../components/elements/InputText';
import { SizeOptionParams } from '../modules/AssetModel/hook';
import { getOptionEntryFields } from 'services/asset';
import { FieldAssetType, ParamGetAssetModels } from '../../type';
import { useDebouncedCallback } from 'use-debounce';

type InputAutoCompleteProps = {
  onChange: (value: Partial<FieldAssetType>) => void;
  placeholder: string;
  value: string;
  disabled?: boolean;
  error?: string;
};

export const InputAutoComplete = ({
  value,
  onChange,
  placeholder,
  disabled,
  error
}: InputAutoCompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [optionEntryField, setOptionEntryField] = useState<FieldAssetType[]>([]);
  const [hasMoreOptionEntryFields, setHasMoreOptionEntryFields] = useState<boolean>(false);
  const [pageOptionEntryFields, setPageOptionEntryFields] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [keySearch, setKeySearch] = useState<string>('');
  const handleFetchMoreOptionType = useCallback(async () => {
    if (!hasMoreOptionEntryFields || isLoadingMore) return;
    try {
      const paramsConvert = {
        page: pageOptionEntryFields,
        size: SizeOptionParams,
        keyword: keySearch
      };
      setIsLoadingMore(true);
      const data = (await getOptionEntryFields(paramsConvert)) as {
        fields: FieldAssetType[];
        total: number;
      };

      setOptionEntryField([...optionEntryField, ...(data.fields || [])]);
      if (data.total <= SizeOptionParams * pageOptionEntryFields) {
        setHasMoreOptionEntryFields(false);
        return;
      }
      setHasMoreOptionEntryFields(true);
      setPageOptionEntryFields(pageOptionEntryFields + 1);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreOptionEntryFields, isLoadingMore, pageOptionEntryFields, optionEntryField, keySearch]);
  /**
   * getOptionEntryFields
   */
  const handleOptionEntryFields = useCallback(async (params: Partial<ParamGetAssetModels>) => {
    const data = (await getOptionEntryFields(params)) as {
      fields: FieldAssetType[];
      total: number;
    };
    setOptionEntryField(data?.fields || []);

    if (data.total <= SizeOptionParams) {
      setHasMoreOptionEntryFields(false);
      return;
    }
    setHasMoreOptionEntryFields(true);
    setPageOptionEntryFields(2);
  }, []);

  useEffect(() => {
    const paramsConvert = {
      page: 1,
      size: SizeOptionParams
    };
    handleOptionEntryFields(paramsConvert);
  }, [handleOptionEntryFields]);

  const onSearchTextType = useDebouncedCallback(async (valueSearch: string) => {
    setKeySearch(valueSearch);
    const paramsConvert = {
      page: 1,
      size: SizeOptionParams,
      keyword: valueSearch
    };
    await handleOptionEntryFields(paramsConvert);
  }, 300);

  return (
    <StyledFormControl>
      <InputText
        inputError={error}
        placeholder={placeholder}
        inputProps={{
          disabled: disabled,
          value: value,
          style: { minHeight: '46px', height: '46px' },
          onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onSearchTextType(e?.target?.value as string);
            const option = optionEntryField.find((item) => item.name === e?.target?.value);
            if (!option) {
              onChange({ name: e?.target?.value });
              return;
            }
            onChange(option);
          },
          onFocus: () => {
            setIsOpen(true);
          },
          onBlur: () => setTimeout(() => setIsOpen(false), 200)
        }}
      />
      {isOpen && optionEntryField.length > 0 && (
        <StyledOptionList id={'id-table-clients'}>
          <InfiniteScroll
            scrollableTarget="id-table-clients"
            dataLength={optionEntryField.length}
            next={handleFetchMoreOptionType}
            hasMore={hasMoreOptionEntryFields}
            loader={
              <Box p={1}>
                <CircularProgress size={24} />
              </Box>
            }>
            {optionEntryField.map((op, index) => {
              return (
                <MenuItemStyled
                  key={index}
                  onMouseDown={() => {
                    onSearchTextType(op.name);
                    const option = optionEntryField.find((item) => item.name === op.name);
                    if (!option) return;
                    onChange(option);
                    setIsOpen(false);
                  }}>
                  <TextStyled>{op.name}</TextStyled>
                </MenuItemStyled>
              );
            })}
          </InfiniteScroll>
        </StyledOptionList>
      )}
    </StyledFormControl>
  );
};

// Styled Components
const StyledFormControl = styled(FormControl)(() => ({
  position: 'relative',
  width: '100%'
}));

const StyledOptionList = styled(Box)(() => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  maxHeight: '300px',
  overflow: 'auto',
  background: '#fff',
  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
  zIndex: 10,
  borderRadius: '4px'
}));

const MenuItemStyled = styled(MenuItem)(() => ({
  height: 48,
  borderBottom: `1px solid #e0e0e0`,
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const TextStyled = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: '#000'
}));
